import type { FC, ReactNode } from 'react'

import { InlineEditableTextDisplay } from './inline-editable-text-display.atom'
import { InlineEditableTextInput } from './inline-editable-text-input.atom'
import { useInlineEditableText } from './use-inline-editable-text.hook'

export interface InlineEditableTextProps {
  value: string
  isEditing: boolean
  isLoading?: boolean
  maxLength?: number
  className?: string
  inputClassName?: string
  onSave: (newValue: string) => void
  onCancel: () => void
  onEditStart?: () => void
}

/**
 * Inline editable text atom that displays text normally and transforms into
 * an input field when in edit mode. Supports Enter to save, Escape to cancel.
 */
export const InlineEditableText: FC<InlineEditableTextProps> = ({
  value,
  isEditing,
  isLoading = false,
  maxLength = 200,
  className,
  inputClassName,
  onSave,
  onCancel,
  onEditStart,
}): ReactNode => {
  const editState = useInlineEditableText({ value, isEditing, isLoading, onSave, onCancel })

  if (isEditing) {
    return (
      <InlineEditableTextInput
        inputRef={editState.inputRef}
        draftValue={editState.draftValue}
        isLoading={isLoading}
        maxLength={maxLength}
        className={className}
        inputClassName={inputClassName}
        onDraftChange={editState.setDraftValue}
        onKeyDown={editState.handleKeyDown}
        onBlur={editState.handleBlur}
      />
    )
  }

  return <InlineEditableTextDisplay value={value} className={className} onEditStart={onEditStart} />
}

InlineEditableText.displayName = 'InlineEditableText'
